import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Trash2, AlertCircle, ArrowRight } from "lucide-react";

function getResultBadgeClass(status, result) {
  if (status === "completed" && result === "passed") return "bg-green-50 text-green-600 border-green-200";
  if (status === "completed" && result === "failed") return "bg-red-50 text-red-600 border-red-200";
  if (status === "running") return "bg-amber-50 text-amber-600 border-amber-200";
  if (status === "failed") return "bg-red-50 text-red-600 border-red-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function getResultLabel(status, result) {
  if (status === "completed") return result || "completed";
  return status;
}

export function RecentExecutions({
  executions,
  getStatusColor,
  onOpenExecution,
  onDeleteExecution,
}) {
  return (
    <div>
      <h2
        className="text-lg font-semibold mb-4 text-slate-900"
        style={{ fontFamily: "Unbounded, sans-serif" }}
      >
        Recent Executions
      </h2>
      {executions.length === 0 ? (
        <Card className="glass-card rounded-xl">
          <CardContent className="p-8 text-center">
            <p className="text-slate-400 text-sm">
              No executions yet. Run a test to see results here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {executions.slice(0, 5).map((execution, idx) => {
            const isFailed = execution.result === "failed" || (execution.status === "completed" && execution.result === "failed");
            const failureReason = execution.failure_reason || execution.error_message ||
              (execution.logs && execution.logs.find(l => l.level === "error")?.message);

            return (
              <motion.div
                key={execution.id}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
              >
                <Card
                  className={`glass-card rounded-xl hover:shadow-md transition-all cursor-pointer ${isFailed ? "border-red-200" : ""}`}
                  onClick={() => onOpenExecution(execution.id)}
                  data-testid={`execution-${execution.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isFailed ? "bg-red-50" : "bg-[#00b37a]/10"}`}>
                          {isFailed
                            ? <AlertCircle className="w-5 h-5 text-red-500" />
                            : <Play className="w-5 h-5 text-[#00b37a]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">
                            {execution.test_name || execution.test_case_name || `Test ${execution.test_case_id?.slice(0, 6) || execution.id?.slice(0, 6)}...`}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            ID: {execution.id?.slice(0, 8)}… · {execution.start_time ? new Date(execution.start_time).toLocaleString() : ""}
                          </p>
                          {isFailed && failureReason && (
                            <p className="text-xs text-red-500 mt-1 truncate flex items-center gap-1">
                              <span className="font-medium">Error:</span> {failureReason}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className={getResultBadgeClass(execution.status, execution.result)}>
                          {getResultLabel(execution.status, execution.result)}
                        </Badge>
                        {isFailed && (
                          <button
                            className="text-xs text-red-500 hover:text-red-600 flex items-center gap-0.5 font-medium whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); onOpenExecution(execution.id); }}
                          >
                            View details
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        )}
                        <Button
                          data-testid={`delete-execution-btn-${execution.id}`}
                          size="sm"
                          variant="ghost"
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md w-8 h-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteExecution(execution.id);
                          }}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
